import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ArrowRight, ChevronRight, Facebook, Globe, Mail, MapPin, Phone } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function Contact() {
    return (
        <LandingLayout>
            <SeoHead
                title="Contact"
                description="Get in touch with Northeastern College in Santiago City, Isabela. Find our address, email, and social channels."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                 
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/contact.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                     
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                     
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                    {/* Glowing Orbs */}
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Contact Us</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Contact Us
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Get in touch with us for admissions, programs, and general student services inquiries.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="space-y-8">
                        {/* Maps Grid */}
                        <div className="grid gap-6 lg:grid-cols-2">
                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
                                
                                <div className="border-border bg-muted/20 border-b p-4">
                                    <h3 className="text-foreground flex items-center gap-2 text-sm font-bold">
                                        <MapPin className="size-4 text-[#0A7A35]" />
                                        Campus Map
                                    </h3>
                                </div>
                                <div className="bg-muted aspect-[4/3] w-full">
                                    <iframe
                                        title="Northeastern College Location"
                                        src="https://www.google.com/maps?q=Northeastern%20College%2C%20Santiago%20City%2C%20Isabela&output=embed"
                                        loading="lazy"
                                        className="h-full w-full border-0"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
                                
                                <div className="border-border bg-muted/20 border-b p-4">
                                    <h3 className="text-foreground flex items-center gap-2 text-sm font-bold">
                                        <Globe className="size-4 text-[#0A7A35]" />
                                        Street View
                                    </h3>
                                </div>
                                <div className="bg-muted aspect-[4/3] w-full">
                                    <iframe
                                        title="Northeastern College Street View"
                                        src="https://maps.google.com/maps?layer=c&cbll=16.6901401,121.5524714&cbp=12,0,0,0,0&output=svembed"
                                        loading="lazy"
                                        className="h-full w-full border-0"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Info blocks */}
                        <div className="grid items-stretch gap-6 lg:grid-cols-2">
                            <div className="bg-card border-border relative h-full rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                                <div className="absolute top-0 left-0 h-full w-1.5 bg-[#0A7A35]" />
                                <div className="flex items-start gap-4 pl-2">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0A7A35]/10 text-[#0A7A35]">
                                        <Phone className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-foreground mb-1 text-xl font-bold">Admissions & Inquiries</h2>
                                        <p className="text-muted-foreground mb-6 text-sm">
                                            We are happy to answer your questions. Get in touch via email or message us on Facebook for the quickest
                                            assistance.
                                        </p>
                                        <div className="flex flex-wrap gap-3">
                                            <a
                                                href="https://mail.google.com/mail/?view=cm&fs=1&to=support@northeasterncollege.edu.ph&su=Inquiry%20-%20Northeastern%20College"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2.5 rounded-full bg-[#0A7A35] px-5 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#08662C] hover:shadow-lg"
                                            >
                                                Email Support
                                                <Mail className="h-4 w-4" />
                                            </a>
                                            <a
                                                href="https://www.facebook.com/NCnianAko"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border-border text-foreground hover:bg-muted inline-flex items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-semibold transition-colors"
                                            >
                                                Message Facebook
                                                <Facebook className="text-primary h-4 w-4" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-card border-border relative h-full rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                                <div className="absolute top-0 left-0 h-full w-1.5 bg-[#0A7A35]" />
                                <div className="pl-2">
                                    <h2 className="text-foreground mb-2 text-xl font-bold">Looking for Admissions Details?</h2>
                                    <p className="text-muted-foreground mb-8 text-sm">
                                        Browse our comprehensive list of academic course offerings, available scholarships, and grants before
                                        submitting your inquiries.
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        <Link
                                            href="/course-offerings"
                                            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-md transition-all hover:shadow-lg"
                                        >
                                            Course Offerings
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                        <Link
                                            href="/scholarships-and-grants"
                                            className="border-border text-foreground hover:bg-muted inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-colors"
                                        >
                                            Scholarships & Grants
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
